import type { UtilityToolDefinition } from '@saas-pages/shared/public/platform-legacy'
import { ruTranslations, type Translations } from '@/shared/i18n/ru'

export type ToolKey =
  | 'textDiff'
  | 'jsonFormatter'
  | 'jsonMinifier'
  | 'yamlFormatter'
  | 'htmlFormatter'
  | 'javascriptFormatter'
  | 'cssFormatter'
  | 'sqlFormatter'
  | 'xmlFormatter'
  | 'csvToJson'
  | 'jsonToCsv'
  | 'base64'
  | 'htmlEntities'
  | 'urlCodec'
  | 'utmGenerator'
  | 'uuidGenerator'
  | 'regexTester'
  | 'cronGenerator'
  | 'jwtDecoder'
  | 'textHashGenerator'
  | 'markdownEditor'

export type ToolStatus = 'available' | 'coming-soon'
export type ToolCategory = 'format' | 'encode' | 'generate' | 'test'

export interface ToolDefinition extends UtilityToolDefinition<ToolKey, ToolCategory> {}

export const CATEGORY_ACCENT: Record<ToolCategory, string> = {
  format:
    'border-violet-300 bg-violet-50 text-violet-900 dark:border-violet-700 dark:bg-violet-950/45 dark:text-violet-100',
  encode:
    'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/45 dark:text-amber-100',
  generate:
    'border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-700 dark:bg-emerald-950/45 dark:text-emerald-100',
  test:
    'border-cyan-300 bg-cyan-50 text-cyan-900 dark:border-cyan-700 dark:bg-cyan-950/45 dark:text-cyan-100',
}

export function buildCategoryLabel(t: Translations): Record<ToolCategory, string> {
  return {
    format: t.categories.format,
    encode: t.categories.encode,
    generate: t.categories.generate,
    test: t.categories.test,
  }
}

export function buildTools(t: Translations): readonly ToolDefinition[] {
  return [
    {
      key: 'textDiff',
      path: '/text-diff',
      analyticsKey: 'text-diff',
      category: 'test',
      title: t.tools.textDiff.title,
      shortDescription: t.tools.textDiff.shortDescription,
      longDescription: t.tools.textDiff.longDescription,
      icon: '±',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/text-diff',
        title: t.tools.textDiff.seoTitle,
        description: t.tools.textDiff.seoDescription,
        changefreq: 'monthly',
        priority: 0.85,
      },
      useCases: t.tools.textDiff.useCases,
      howTo: t.tools.textDiff.howTo,
      faq: t.tools.textDiff.faq,
      relatedTools: ['regexTester', 'jsonFormatter', 'yamlFormatter'],
    },
    {
      key: 'jsonFormatter',
      path: '/json-formatter',
      analyticsKey: 'json-formatter',
      category: 'format',
      title: t.tools.jsonFormatter.title,
      shortDescription: t.tools.jsonFormatter.shortDescription,
      longDescription: t.tools.jsonFormatter.longDescription,
      icon: '{}',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/json-formatter',
        title: t.tools.jsonFormatter.seoTitle,
        description: t.tools.jsonFormatter.seoDescription,
        changefreq: 'monthly',
        priority: 0.9,
      },
      useCases: t.tools.jsonFormatter.useCases,
      howTo: t.tools.jsonFormatter.howTo,
      faq: t.tools.jsonFormatter.faq,
      relatedTools: ['jsonMinifier', 'jsonToCsv', 'yamlFormatter'],
    },
    {
      key: 'jsonMinifier',
      path: '/json-minifier',
      analyticsKey: 'json-minifier',
      category: 'format',
      title: t.tools.jsonMinifier.title,
      shortDescription: t.tools.jsonMinifier.shortDescription,
      longDescription: t.tools.jsonMinifier.longDescription,
      icon: '{}',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/json-minifier',
        title: t.tools.jsonMinifier.seoTitle,
        description: t.tools.jsonMinifier.seoDescription,
        changefreq: 'monthly',
        priority: 0.85,
      },
      useCases: t.tools.jsonMinifier.useCases,
      howTo: t.tools.jsonMinifier.howTo,
      faq: t.tools.jsonMinifier.faq,
      relatedTools: ['jsonFormatter', 'jsonToCsv', 'urlCodec'],
    },
    {
      key: 'yamlFormatter',
      path: '/yaml-formatter',
      analyticsKey: 'yaml-formatter',
      category: 'format',
      title: t.tools.yamlFormatter.title,
      shortDescription: t.tools.yamlFormatter.shortDescription,
      longDescription: t.tools.yamlFormatter.longDescription,
      icon: 'YML',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/yaml-formatter',
        title: t.tools.yamlFormatter.seoTitle,
        description: t.tools.yamlFormatter.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.yamlFormatter.useCases,
      howTo: t.tools.yamlFormatter.howTo,
      faq: t.tools.yamlFormatter.faq,
    },
    {
      key: 'htmlFormatter',
      path: '/html-formatter',
      analyticsKey: 'html-formatter',
      category: 'format',
      title: t.tools.htmlFormatter.title,
      shortDescription: t.tools.htmlFormatter.shortDescription,
      longDescription: t.tools.htmlFormatter.longDescription,
      icon: '<>',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/html-formatter',
        title: t.tools.htmlFormatter.seoTitle,
        description: t.tools.htmlFormatter.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.htmlFormatter.useCases,
      howTo: t.tools.htmlFormatter.howTo,
      faq: t.tools.htmlFormatter.faq,
    },
    {
      key: 'javascriptFormatter',
      path: '/javascript-formatter',
      analyticsKey: 'javascript-formatter',
      category: 'format',
      title: t.tools.javascriptFormatter.title,
      shortDescription: t.tools.javascriptFormatter.shortDescription,
      longDescription: t.tools.javascriptFormatter.longDescription,
      icon: 'JS',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/javascript-formatter',
        title: t.tools.javascriptFormatter.seoTitle,
        description: t.tools.javascriptFormatter.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.javascriptFormatter.useCases,
      howTo: t.tools.javascriptFormatter.howTo,
      faq: t.tools.javascriptFormatter.faq,
      relatedTools: ['cssFormatter', 'htmlFormatter', 'regexTester'],
    },
    {
      key: 'cssFormatter',
      path: '/css-formatter',
      analyticsKey: 'css-formatter',
      category: 'format',
      title: t.tools.cssFormatter.title,
      shortDescription: t.tools.cssFormatter.shortDescription,
      longDescription: t.tools.cssFormatter.longDescription,
      icon: 'CSS',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/css-formatter',
        title: t.tools.cssFormatter.seoTitle,
        description: t.tools.cssFormatter.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.cssFormatter.useCases,
      howTo: t.tools.cssFormatter.howTo,
      faq: t.tools.cssFormatter.faq,
      relatedTools: ['htmlFormatter', 'javascriptFormatter', 'xmlFormatter'],
    },
    {
      key: 'sqlFormatter',
      path: '/sql-formatter',
      analyticsKey: 'sql-formatter',
      category: 'format',
      title: t.tools.sqlFormatter.title,
      shortDescription: t.tools.sqlFormatter.shortDescription,
      longDescription: t.tools.sqlFormatter.longDescription,
      icon: 'SQL',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/sql-formatter',
        title: t.tools.sqlFormatter.seoTitle,
        description: t.tools.sqlFormatter.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.sqlFormatter.useCases,
      howTo: t.tools.sqlFormatter.howTo,
      faq: t.tools.sqlFormatter.faq,
    },
    {
      key: 'xmlFormatter',
      path: '/xml-formatter',
      analyticsKey: 'xml-formatter',
      category: 'format',
      title: t.tools.xmlFormatter.title,
      shortDescription: t.tools.xmlFormatter.shortDescription,
      longDescription: t.tools.xmlFormatter.longDescription,
      icon: 'XML',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/xml-formatter',
        title: t.tools.xmlFormatter.seoTitle,
        description: t.tools.xmlFormatter.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.xmlFormatter.useCases,
      howTo: t.tools.xmlFormatter.howTo,
      faq: t.tools.xmlFormatter.faq,
    },
    {
      key: 'csvToJson',
      path: '/csv-v-json',
      analyticsKey: 'csv-to-json',
      category: 'format',
      title: t.tools.csvToJson.title,
      shortDescription: t.tools.csvToJson.shortDescription,
      longDescription: t.tools.csvToJson.longDescription,
      icon: 'CSV',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/csv-v-json',
        title: t.tools.csvToJson.seoTitle,
        description: t.tools.csvToJson.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.csvToJson.useCases,
      howTo: t.tools.csvToJson.howTo,
      faq: t.tools.csvToJson.faq,
      relatedTools: ['jsonToCsv', 'jsonFormatter', 'xmlFormatter'],
    },
    {
      key: 'jsonToCsv',
      path: '/json-v-csv',
      analyticsKey: 'json-to-csv',
      category: 'format',
      title: t.tools.jsonToCsv.title,
      shortDescription: t.tools.jsonToCsv.shortDescription,
      longDescription: t.tools.jsonToCsv.longDescription,
      icon: 'JSC',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/json-v-csv',
        title: t.tools.jsonToCsv.seoTitle,
        description: t.tools.jsonToCsv.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.jsonToCsv.useCases,
      howTo: t.tools.jsonToCsv.howTo,
      faq: t.tools.jsonToCsv.faq,
      relatedTools: ['csvToJson', 'jsonFormatter', 'yamlFormatter'],
    },
    {
      key: 'base64',
      path: '/base64',
      analyticsKey: 'base64',
      category: 'encode',
      title: t.tools.base64.title,
      shortDescription: t.tools.base64.shortDescription,
      longDescription: t.tools.base64.longDescription,
      icon: '64',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/base64',
        title: t.tools.base64.seoTitle,
        description: t.tools.base64.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.base64.useCases,
      howTo: t.tools.base64.howTo,
      faq: t.tools.base64.faq,
    },
    {
      key: 'htmlEntities',
      path: '/html-entities',
      analyticsKey: 'html-entities',
      category: 'encode',
      title: t.tools.htmlEntities.title,
      shortDescription: t.tools.htmlEntities.shortDescription,
      longDescription: t.tools.htmlEntities.longDescription,
      icon: '&;',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/html-entities',
        title: t.tools.htmlEntities.seoTitle,
        description: t.tools.htmlEntities.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.htmlEntities.useCases,
      howTo: t.tools.htmlEntities.howTo,
      faq: t.tools.htmlEntities.faq,
    },
    {
      key: 'urlCodec',
      path: '/url-encode-decode',
      analyticsKey: 'url-codec',
      category: 'encode',
      title: t.tools.urlCodec.title,
      shortDescription: t.tools.urlCodec.shortDescription,
      longDescription: t.tools.urlCodec.longDescription,
      icon: '%',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/url-encode-decode',
        title: t.tools.urlCodec.seoTitle,
        description: t.tools.urlCodec.seoDescription,
        changefreq: 'monthly',
        priority: 0.7,
      },
      useCases: t.tools.urlCodec.useCases,
      howTo: t.tools.urlCodec.howTo,
      faq: t.tools.urlCodec.faq,
      relatedTools: ['base64', 'htmlEntities', 'jsonFormatter'],
    },
    {
      key: 'utmGenerator',
      path: '/utm-generator',
      analyticsKey: 'utm-generator',
      category: 'generate',
      title: t.tools.utmGenerator.title,
      shortDescription: t.tools.utmGenerator.shortDescription,
      longDescription: t.tools.utmGenerator.longDescription,
      icon: 'UTM',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/utm-generator',
        title: t.tools.utmGenerator.seoTitle,
        description: t.tools.utmGenerator.seoDescription,
        changefreq: 'monthly',
        priority: 0.85,
      },
      useCases: t.tools.utmGenerator.useCases,
      howTo: t.tools.utmGenerator.howTo,
      faq: t.tools.utmGenerator.faq,
      relatedTools: ['urlCodec'],
    },
    {
      key: 'uuidGenerator',
      path: '/uuid-generator',
      analyticsKey: 'uuid-generator',
      category: 'generate',
      title: t.tools.uuidGenerator.title,
      shortDescription: t.tools.uuidGenerator.shortDescription,
      longDescription: t.tools.uuidGenerator.longDescription,
      icon: 'ID',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/uuid-generator',
        title: t.tools.uuidGenerator.seoTitle,
        description: t.tools.uuidGenerator.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.uuidGenerator.useCases,
      howTo: t.tools.uuidGenerator.howTo,
      faq: t.tools.uuidGenerator.faq,
    },
    {
      key: 'regexTester',
      path: '/regex-tester',
      analyticsKey: 'regex-tester',
      category: 'test',
      title: t.tools.regexTester.title,
      shortDescription: t.tools.regexTester.shortDescription,
      longDescription: t.tools.regexTester.longDescription,
      icon: '.*',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/regex-tester',
        title: t.tools.regexTester.seoTitle,
        description: t.tools.regexTester.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.regexTester.useCases,
      howTo: t.tools.regexTester.howTo,
      faq: t.tools.regexTester.faq,
    },
    {
      key: 'jwtDecoder',
      path: '/jwt-decoder',
      analyticsKey: 'jwt-decoder',
      category: 'encode',
      title: t.tools.jwtDecoder.title,
      shortDescription: t.tools.jwtDecoder.shortDescription,
      longDescription: t.tools.jwtDecoder.longDescription,
      icon: 'JWT',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/jwt-decoder',
        title: t.tools.jwtDecoder.seoTitle,
        description: t.tools.jwtDecoder.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.jwtDecoder.useCases,
      howTo: t.tools.jwtDecoder.howTo,
      faq: t.tools.jwtDecoder.faq,
      relatedTools: ['base64', 'jsonFormatter', 'urlCodec'],
    },
    {
      key: 'cronGenerator',
      path: '/cron-generator',
      analyticsKey: 'cron-generator',
      category: 'generate',
      title: t.tools.cronGenerator.title,
      shortDescription: t.tools.cronGenerator.shortDescription,
      longDescription: t.tools.cronGenerator.longDescription,
      icon: 'CRON',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/cron-generator',
        title: t.tools.cronGenerator.seoTitle,
        description: t.tools.cronGenerator.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.cronGenerator.useCases,
      howTo: t.tools.cronGenerator.howTo,
      faq: t.tools.cronGenerator.faq,
    },
    {
      key: 'markdownEditor',
      path: '/markdown-editor',
      analyticsKey: 'markdown-editor',
      category: 'format',
      title: t.tools.markdownEditor.title,
      shortDescription: t.tools.markdownEditor.shortDescription,
      longDescription: t.tools.markdownEditor.longDescription,
      icon: 'MD',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/markdown-editor',
        title: t.tools.markdownEditor.seoTitle,
        description: t.tools.markdownEditor.seoDescription,
        changefreq: 'monthly',
        priority: 0.85,
      },
      useCases: t.tools.markdownEditor.useCases,
      howTo: t.tools.markdownEditor.howTo,
      faq: t.tools.markdownEditor.faq,
      relatedTools: ['htmlFormatter'],
    },
    {
      key: 'textHashGenerator',
      path: '/hash-generator',
      analyticsKey: 'text-hash-generator',
      category: 'generate',
      title: t.tools.textHashGenerator.title,
      shortDescription: t.tools.textHashGenerator.shortDescription,
      longDescription: t.tools.textHashGenerator.longDescription,
      icon: '#',
      status: 'available',
      processing: 'local',
      seo: {
        path: '/hash-generator',
        title: t.tools.textHashGenerator.seoTitle,
        description: t.tools.textHashGenerator.seoDescription,
        changefreq: 'monthly',
        priority: 0.8,
      },
      useCases: t.tools.textHashGenerator.useCases,
      howTo: t.tools.textHashGenerator.howTo,
      faq: t.tools.textHashGenerator.faq,
      relatedTools: ['base64', 'uuidGenerator', 'jwtDecoder'],
    },
  ]
}

export const TOOLS = buildTools(ruTranslations)

export const AVAILABLE_TOOLS = TOOLS.filter((tool) => tool.status === 'available')

export function getTool(
  key: ToolKey,
  tools: readonly ToolDefinition[] = TOOLS,
): ToolDefinition {
  const tool = tools.find((item) => item.key === key)
  if (!tool) {
    throw new Error(`[devdeck tools] Unknown tool key: ${key}`)
  }
  return tool
}

export function relatedTools(
  current: ToolKey,
  tools: readonly ToolDefinition[] = TOOLS,
  limit = 3,
): ToolDefinition[] {
  const currentTool = getTool(current, tools)
  const explicit = currentTool.relatedTools

  if (explicit && explicit.length > 0) {
    return explicit
      .map((key) => tools.find((tool) => tool.key === key))
      .filter((tool): tool is ToolDefinition => Boolean(tool))
      .slice(0, limit)
  }

  return tools
    .filter((tool) => tool.key !== current && tool.category === currentTool.category)
    .concat(tools.filter((tool) => tool.key !== current && tool.category !== currentTool.category))
    .slice(0, limit)
}
